import { IS_DIRECT_VACATIONS } from 'config/envSetup';
import { logoAlluraDirect } from 'config/imageUrls';
import Head from 'next/head';

export interface AppSEOProps {
  /** SEO title, appears in tab of browser
   * Company name will automatically be added after the title
   * Formated like: <title> | <company_name>
   */
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  robots?: string;

  /* Optionally pass additional meta tags for improved SEO */
  children?: React.ReactNode;
}

/** Each individual Nextjs page should use this component. That means top-level components in the pages directory should use this component
 *
 * SEO Resources:
 * https://ogp.me/
 * https://nextjs.org/learn/seo/rendering-and-ranking/on-page-seo
 *   - interestly nextjs docs recommend an H1 tag on every page
 *   - use nextjs Link component
 * https://developer.twitter.com/en/docs/twitter-for-websites/cards/overview/markup
 * Facebook uses open graph tags
 *
 *
 */
export const AppSEO = ({
  title = 'Unknown Page, Provide a Title',
  description = 'Mountain Vacation Rentals',
  keywords = 'vaction, rentals, alluraDirect, whistler, blackcomb',
  robots = 'index,follow',
  image = logoAlluraDirect,
  children,
}: AppSEOProps) => {
  const isDv = IS_DIRECT_VACATIONS();
  const companyName = isDv ? 'DirectVacations' : 'alluraDirect';

  return (
    <Head>
      <title>
        {title} | {companyName}
      </title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content={robots}></meta>

      {/* Open Graph: https://ogp.me/ */}
      <meta property="og:title" content={title} />
      <meta property="og:image" content={image} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />

      {/* Twitter: https://developer.twitter.com/en/docs/twitter-for-websites/cards/overview/markup*/}
      <meta property="twitter:site" content={'@alluradirect'} />
      <meta property="twitter:app:id:ipad" content={'1588310138'} />
      <meta property="twitter:app:id:iphone" content={'1588310138'} />
      <meta property="twitter:app:name:iphone" content={`alluraDirect Vacation Rentals`} />
      <meta property="twitter:app:name:ipad" content={'alluraDirect Vacation Rentals'} />
      <meta property="twitter:app:name:googleplay" content={'alluraDirect Vacation Rentals'} />
      <meta property="twitter:app:id:googleplay" content={'com.alluraDirect'} />

      {/* Pass additional tags */}
      {children}
    </Head>
  );
};
